// Last-known city state (docs/ARCHITECTURE.md §7): a city in sight shows its mirror entity's synced state and the
// buildings in view in it; a city out of sight keeps what the player last saw. Before a city is first seen it shows
// its war-start state (setup owner, content starting HP, no buildings), which is exactly what the sim starts from.

import type { Entity } from '@esengine/ecs-framework'
import { CITIES } from '@taxwar/content'
import {
  Building,
  CityNode,
  Health,
  InvariantViolation,
  NEUTRAL_OWNER,
  Owner,
  type BuildingTypeId,
  type CityIndex,
  type MapCityView,
  type MatchSetupView,
  type OwnerValue,
  type SimId,
} from '@taxwar/net'
import type { CityLiveState } from './contracts'
import { getRequired, type MirrorWorld } from './mirror'

class CityState implements CityLiveState {
  owner: OwnerValue
  hp: number
  maxHp: number
  fallen = false
  captureProgress = 0
  captureBy: OwnerValue = NEUTRAL_OWNER
  /** Replaced (never mutated) when the building set changes, so holders of the old array see a stable value. */
  buildings: readonly BuildingTypeId[] = []
  inSight = false

  constructor(city: MapCityView) {
    this.owner = city.initialOwner
    this.hp = startingCityHp(city)
    this.maxHp = this.hp
  }
}

/** The sim's starting city HP by kind (content CITIES, GDD §5). */
function startingCityHp(city: MapCityView): number {
  if (city.capital) return CITIES.capital.hp
  return city.initialOwner === NEUTRAL_OWNER ? CITIES.neutral.hp : CITIES.owned.hp
}

function sortAscending(list: BuildingTypeId[]): void {
  for (let i = 1; i < list.length; i++) {
    const value = list[i] as BuildingTypeId
    let j = i - 1
    while (j >= 0 && (list[j] as BuildingTypeId) > value) {
      list[j + 1] = list[j] as BuildingTypeId
      j--
    }
    list[j + 1] = value
  }
}

function sameList(a: readonly BuildingTypeId[], b: readonly BuildingTypeId[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

export class CityTracker {
  private readonly states: readonly CityState[]
  private readonly citySimIds: readonly SimId[]
  private readonly cityIndexBySimId: ReadonlyMap<number, CityIndex>
  /** Per city: building types in view this refresh (sorted), reused every refresh. */
  private readonly inView: readonly BuildingTypeId[][]

  constructor(setup: MatchSetupView) {
    this.states = setup.cities.map((city) => new CityState(city))
    this.citySimIds = setup.cities.map((city) => city.simId)
    this.cityIndexBySimId = new Map(setup.cities.map((city) => [city.simId as number, city.index]))
    this.inView = setup.cities.map(() => [])
  }

  state(index: CityIndex): CityLiveState {
    const state = this.states[index]
    if (state === undefined) throw new InvariantViolation(`city index ${index} is not on the map`)
    return state
  }

  /** Re-reads every city from the mirror after a view frame; `changed` is called once per city that changed. */
  refresh(mirror: MirrorWorld, changed: (index: CityIndex) => void): void {
    for (let c = 0; c < this.inView.length; c++) (this.inView[c] as BuildingTypeId[]).length = 0
    const buildings = mirror.buildings
    for (let b = 0; b < buildings.length; b++) {
      const building = getRequired(buildings[b] as Entity, Building)
      const index = this.cityIndexBySimId.get(building.citySimId)
      if (index === undefined) throw new InvariantViolation(`building in unknown city SimId ${building.citySimId}`)
      ;(this.inView[index] as BuildingTypeId[]).push(building.typeId as BuildingTypeId)
    }
    for (let c = 0; c < this.states.length; c++) {
      if (this.refreshCity(mirror, c)) changed(c as CityIndex)
    }
  }

  private refreshCity(mirror: MirrorWorld, c: number): boolean {
    const state = this.states[c] as CityState
    const entity = mirror.find(this.citySimIds[c] as SimId)
    if (entity === undefined) {
      if (!state.inSight) return false
      state.inSight = false
      return true
    }
    const owner = getRequired(entity, Owner).player as OwnerValue
    const health = getRequired(entity, Health)
    const node = getRequired(entity, CityNode)
    const inView = this.inView[c] as BuildingTypeId[]
    sortAscending(inView)
    let dirty = !state.inSight
    if (state.owner !== owner || state.hp !== health.hp || state.maxHp !== health.maxHp) dirty = true
    if (state.fallen !== node.fallen || state.captureProgress !== node.captureProgress || state.captureBy !== node.captureBy) dirty = true
    state.inSight = true
    state.owner = owner
    state.hp = health.hp
    state.maxHp = health.maxHp
    state.fallen = node.fallen
    state.captureProgress = node.captureProgress
    state.captureBy = node.captureBy as OwnerValue
    if (!sameList(state.buildings, inView)) {
      state.buildings = inView.slice()
      dirty = true
    }
    return dirty
  }
}
